import React, { useState, useEffect, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { apiPost } from '../../api/apiClient';
import ChangePasswordForm from './ChangePasswordForm';
import logo from '../login/logo.png';
import RTButton from '../common/RTButton';
import './LoginForm.css';
import tenantStorage from '../../utils/tenantStorage';
import { useSessionInfo, storeMenus } from '../../hooks/useSessionInfo';
import {
    consumePrefillUsername,
    isTenDigitMobile,
    normalizeUsername,
} from '../../utils/authHelpers';
import { setAccessMaps } from '../../utils/permissions';

const modeMessages = {
    SESSION_INACTIVE: 'You have been signed out due to inactivity. Please sign in again.',
    SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
    SESSION_LOGGED_OUT: 'You have been logged out. Please sign in again.',
    PASSWORD_CHANGED: 'Your password has been changed successfully. Please sign in again with your new password.',
};

const extractErrorMessage = (err) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.detail) return detail.detail;
    if (err?.message && err.message.indexOf('401') !== -1) {
        return 'Username or password is incorrect';
    }
    return 'An error occurred while logging in. Please try again.';
};

const LoginForm = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [showWarning, setShowWarning] = useState(true);
    const [mode, setMode] = useState('SESSION_LOGIN');
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const sessionInfo = useSessionInfo();

    const isMobileUser = isTenDigitMobile(username);
    const welcomeTitle = sessionInfo.displayName
        ? `Welcome to ${sessionInfo.displayName}`
        : 'Welcome';

    const persistLogin = useCallback((response) => {
        tenantStorage.setItem('accessToken', response.access_token);
        tenantStorage.setItem('refreshToken', response.refresh_token);
        tenantStorage.setItem('userRole', response.user_role);
        tenantStorage.setItem('username', normalizeUsername(username));
        if (response.role_id != null) {
            tenantStorage.setItem('roleId', String(response.role_id));
        }
        setAccessMaps({
            menuAccess: response.menu_access || {},
            permissions: response.permissions || {},
        });
        if (response.menus) {
            storeMenus(response.menus);
        } else if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('renttick-auth-updated'));
        }
    }, [username]);

    const finishLogin = useCallback((response) => {
        persistLogin(response);
        if (response.must_change_password) {
            setMode('NATIVE_MUST_CHANGE_PASSWORD');
            return;
        }
        navigate('/dashboard');
    }, [navigate, persistLogin]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlMode = params.get('mode');
        const stateMode = location.state?.mode;

        if (urlMode) {
            setMode(urlMode);
        } else {
            setMode(stateMode || 'SESSION_LOGIN');
        }

        const { username: prefill, cleanedSearch } = consumePrefillUsername(location.search);
        if (prefill) {
            setUsername(prefill);
        }

        if (prefill) {
            const newUrl = location.pathname + (cleanedSearch ? `?${cleanedSearch}` : '');
            window.history.replaceState({}, '', newUrl);
        }

        if (stateMode && stateMode !== 'SESSION_LOGIN') {
            setPassword('');
            setOtp('');
            setError('');
            setOtpSent(false);
            setShowWarning(true);
        }
    }, [location.state, location.search, location.pathname]);

    useEffect(() => {
        if (!isMobileUser) {
            setOtp('');
            setOtpSent(false);
        }
    }, [isMobileUser]);

    useEffect(() => {
        const accessToken = tenantStorage.getItem('accessToken');
        if (accessToken && mode !== 'NATIVE_MUST_CHANGE_PASSWORD') {
            navigate('/dashboard');
        }
    }, [navigate, mode]);

    const handleRequestOtp = async () => {
        setShowWarning(false);
        if (!isMobileUser) {
            setError('Enter a 10-digit mobile number to receive OTP');
            return;
        }
        setOtpLoading(true);
        setError('');
        try {
            await apiPost('/auth/native/login/otp/request', {
                username: normalizeUsername(username),
            });
            setOtpSent(true);
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setOtpLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setShowWarning(false);
        setError('');

        if (!username.trim()) {
            setError('Mobile or username is required');
            return;
        }

        const normalized = normalizeUsername(username);
        const otpValue = otp.trim();

        if (isMobileUser && otpValue.length >= 4) {
            setLoading(true);
            try {
                const response = await apiPost('/auth/native/login/otp/verify', {
                    username: normalized,
                    otp: otpValue,
                });
                finishLogin(response.data);
            } catch (err) {
                setError(extractErrorMessage(err));
            } finally {
                setLoading(false);
            }
            return;
        }

        if (!password) {
            setError('Enter your password, or use OTP if you entered a mobile number');
            return;
        }

        setLoading(true);
        try {
            const response = await apiPost('/auth/native/login/password', {
                username: normalized,
                password,
            });
            finishLogin(response.data);
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const renderPasswordToggle = () => (
        <RTButton
            type="button"
            variant="link"
            className="password-toggle-btn"
            iconMode={true}
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
            {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10.584 10.587C10.218 10.958 10 11.459 10 12C10 13.105 10.895 14 12 14C12.541 14 13.042 13.782 13.413 13.416" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9.363 5.365C10.212 5.125 11.106 5 12 5C19 5 23 12 23 12C22.393 13.433 21.374 14.703 20.061 15.692" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6.646 6.646C4.264 8.167 1.789 10.644 1 12C1 12 5 19 12 19C13.394 19 14.716 18.736 15.934 18.264" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            )}
        </RTButton>
    );

    if (mode === 'NATIVE_MUST_CHANGE_PASSWORD') {
        return (
            <ChangePasswordForm
                mode="NATIVE_MUST_CHANGE_PASSWORD"
                username={normalizeUsername(username)}
            />
        );
    }

    return (
        <div className="login-container">
            <div>
                <img src={logo} alt="App Logo" className="login-logo" />
            </div>
            <Form onSubmit={handleSubmit} className="login-form">
                <h5 className="text-center mb-3 login-welcome">{welcomeTitle}</h5>
                {modeMessages[mode] && showWarning && (
                    <div className={`alert text-center ${mode === 'PASSWORD_CHANGED' ? 'alert-success' : 'alert-info'}`}>
                        {modeMessages[mode]}
                    </div>
                )}
                {error && <div className="alert alert-danger">{error}</div>}

                <Form.Group controlId="formUsername" className="form-group">
                    <Form.Label>Mobile/Username</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Mobile number or username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        autoComplete="username"
                    />
                </Form.Group>

                <Form.Group controlId="formPassword" className="form-group">
                    <Form.Label>Password</Form.Label>
                    <div className="password-input-container">
                        <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="password-input"
                            autoComplete="current-password"
                        />
                        {renderPasswordToggle()}
                    </div>
                </Form.Group>

                {isMobileUser && (
                    <>
                        <div className="login-or-divider">OR</div>
                        <Form.Group controlId="formOtp" className="form-group">
                            <Form.Label>OTP</Form.Label>
                            <div className="otp-input-container">
                                <Form.Control
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="6-digit OTP"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="otp-input"
                                    maxLength={6}
                                    autoComplete="one-time-code"
                                />
                                <RTButton
                                    type="link"
                                    variant="secondary"
                                    className="otp-inline-btn"
                                    onClick={handleRequestOtp}
                                    disabled={otpLoading || loading}
                                >
                                    {otpLoading ? 'Sending…' : (otpSent ? 'Resend OTP' : 'Get OTP')}
                                </RTButton>
                            </div>
                        </Form.Group>
                    </>
                )}

                <RTButton
                    type="submit"
                    className="w-100 login-submit-btn"
                    disabled={loading || otpLoading}
                >
                    {loading ? 'Signing in…' : 'Login'}
                </RTButton>

                <div className="forgot-password-container">
                    <Link to="/login/forgot-password" className="forgot-password-link">
                        Forgot password?
                    </Link>
                </div>
            </Form>

            <div className="terms-link-container">
                <a href="/terms" className="terms-link">Terms & Conditions</a>
                <span className="link-separator">•</span>
                <a href="/privacy" className="terms-link">Privacy Policy</a>
            </div>
        </div>
    );
};

export default LoginForm;
